// Newsletter Popup functionality for Shopify theme
class NewsletterPopup {
  constructor() {
    this.popup = null;
    this.overlay = null;
    this.form = null;
    this.isVisible = false;
    this.shownBefore = false;
    this.init();
  }

  init() {
    this.popup = document.getElementById("newsletter-popup-overlay");
    this.overlay = this.popup;
    this.form = document.getElementById("newsletter-popup-form");

    if (!this.popup) {
      console.warn("Newsletter popup element not found");
      return;
    }

    // Check if popup is enabled
    if (
      this.popup.style.display === "none" ||
      this.popup.classList.contains("hidden")
    ) {
      // Popup might be disabled via section settings
      const section = this.popup.closest(".newsletter_popup-section");
      if (section && section.style.display === "none") {
        console.log("Newsletter popup is disabled via section settings");
        return;
      }
    }

    this.bindEvents();
    this.checkShowConditions();
  }

  bindEvents() {
    // Close button
    const closeBtn = document.getElementById("newsletter-popup-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => this.hide());
    }

    // Close on overlay click
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.hide();
      }
    });

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVisible) {
        this.hide();
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleFormSubmit(e));
    }

    // Trigger buttons
    document.addEventListener("click", (e) => {
      const triggerBtn = e.target.closest("[data-show-newsletter-popup]");
      if (triggerBtn) {
        e.preventDefault();
        this.show();
      }
    });

    // Auto-show triggers
    this.setupAutoShowTriggers();
  }

  setupAutoShowTriggers() {
    // Show after page load delay
    const delay = parseInt(this.popup.dataset.delay || "3000");
    const showOnScroll = this.popup.dataset.showOnScroll === "true";
    const scrollPercentage = parseInt(
      this.popup.dataset.scrollPercentage || "50",
    );

    if (delay > 0) {
      setTimeout(() => {
        if (!this.shownBefore && !this.isVisible) {
          this.show();
        }
      }, delay);
    }

    if (showOnScroll) {
      let scrollTriggered = false;
      window.addEventListener("scroll", () => {
        if (scrollTriggered || this.shownBefore || this.isVisible) return;

        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const documentHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = (scrollTop / documentHeight) * 100;

        if (scrollPercent >= scrollPercentage) {
          scrollTriggered = true;
          this.show();
        }
      });
    }
  }

  checkShowConditions() {
    // Don't show if already subscribed
    if (window.customer && window.customer.accepts_marketing) {
      return;
    }

    // Check if popup was dismissed recently
    const dismissed = localStorage.getItem("newsletter-popup-dismissed");
    const dismissTime = dismissed ? parseInt(dismissed) : 0;
    const now = Date.now();
    const dismissDuration = 7 * 24 * 60 * 60 * 1000; // 7 days

    if (now - dismissTime < dismissDuration) {
      return;
    }
  }

  show() {
    if (this.isVisible) return;

    this.popup.classList.remove("hidden");
    this.isVisible = true;
    this.shownBefore = true;

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Focus first input
    const firstInput = this.form?.querySelector(
      'input[type="text"], input[type="email"]',
    );
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }

    // Track popup view
    this.trackEvent("popup_viewed");
  }

  hide() {
    if (!this.isVisible) return;

    this.popup.classList.add("hidden");
    this.isVisible = false;

    // Restore body scroll
    document.body.style.overflow = "";

    // Track popup dismissal
    this.trackEvent("popup_dismissed");

    // Remember dismissal
    localStorage.setItem("newsletter-popup-dismissed", Date.now().toString());
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    const submitBtn = this.form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;

    // Show loading state
    submitBtn.disabled = true;
    submitBtn.textContent = "Wird abonniert...";
    submitBtn.classList.add("opacity-75");

    try {
      const formData = new FormData(this.form);

      // Add Shopify customer form action
      formData.append("form_type", "customer");
      formData.append("utf8", "✓");

      const response = await fetch("/contact", {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const result = await response.text();

        // Check if form was successful
        if (result.includes("success") || result.includes("erfolgreich")) {
          this.showSuccessMessage();
          this.trackEvent("newsletter_subscribed");

          // Hide popup after success
          setTimeout(() => {
            this.hide();
          }, 2000);
        } else {
          this.showErrorMessage(
            "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
          );
        }
      } else {
        throw new Error("Network error");
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      this.showErrorMessage(
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
      );
    } finally {
      // Restore button state
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
      submitBtn.classList.remove("opacity-75");
    }
  }

  showSuccessMessage() {
    // Remove existing messages
    const existingMessages = this.form.querySelectorAll(".newsletter-message");
    existingMessages.forEach((msg) => msg.remove());

    // Create success message
    const successDiv = document.createElement("div");
    successDiv.className =
      "newsletter-message bg-green-50 border border-green-200 rounded-lg p-4 mb-6";
    successDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-green-800">Newsletter erfolgreich abonniert!</p>
          <p class="text-sm text-green-700 mt-1">Vielen Dank für Ihr Interesse.</p>
        </div>
      </div>
    `;

    // Insert at top of form
    this.form.insertBefore(successDiv, this.form.firstChild);
  }

  showErrorMessage(message) {
    // Remove existing messages
    const existingMessages = this.form.querySelectorAll(".newsletter-message");
    existingMessages.forEach((msg) => msg.remove());

    // Create error message
    const errorDiv = document.createElement("div");
    errorDiv.className =
      "newsletter-message bg-red-50 border border-red-200 rounded-lg p-4 mb-6";
    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Fehler beim Abonnieren</h3>
          <p class="text-sm text-red-700 mt-1">${message}</p>
        </div>
      </div>
    `;

    // Insert at top of form
    this.form.insertBefore(errorDiv, this.form.firstChild);
  }

  trackEvent(eventName) {
    // Track popup events for analytics
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, {
        event_category: "newsletter_popup",
        event_label: "popup_interaction",
      });
    }

    // Custom event for other tracking systems
    window.dispatchEvent(
      new CustomEvent("newsletter-popup-event", {
        detail: { event: eventName },
      }),
    );
  }

  // Public methods for external control
  showPopup() {
    this.show();
  }

  hidePopup() {
    this.hide();
  }

  resetDismissal() {
    localStorage.removeItem("newsletter-popup-dismissed");
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.NewsletterPopup = new NewsletterPopup();
});

// Export for module systems
if (typeof module !== "undefined" && module.exports) {
  module.exports = NewsletterPopup;
}
